import React, { useState, useEffect, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import { Routes, Route, useLocation } from 'react-router-dom'; // Import routing components and hook

// Components
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Projects from './components/Projects';
import Upload from './components/Upload';
import Documents from './components/Documents';
import Chat from './components/Chat';
import Integrations from './components/Integrations';
import QueryTableFullScreen from './components/QueryTableFullScreen';
import NotificationContainer from './components/NotificationContainer';
import AuthWrapper from './components/auth/AuthWrapper';

// Theme
import { ThemeProvider } from './contexts/ThemeContext';

// Services
import { apiService } from './services/apiService';
import { notificationService } from './services/notificationService';

// Types
import { Project, Document } from './types';

function MainApp() {
  const location = useLocation(); // Hook to get current location
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showQueryTableFullScreen, setShowQueryTableFullScreen] = useState<boolean>(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState<boolean>(false);

  const loadDashboardData = useCallback(async () => {
    try {
      // Load dashboard data - this will be implemented in Dashboard component
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      notificationService.show('Failed to load dashboard data', 'error');
    } finally {
    }
  }, []);

  const loadDocuments = useCallback(async () => {
    try {
      const docs = await apiService.get('/documents/');
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      notificationService.show('Failed to load documents', 'error');
    }
  }, [setDocuments]);

  const refreshProcessingDocuments = useCallback(async () => {
    // Only refresh if on the documents page
    if (location.pathname === '/documents') {
      const processingDocs = documents.filter((doc: Document) => doc.status === 'processing');
      if (processingDocs.length > 0) {
        loadDocuments();
      }
    }
  }, [loadDocuments, location.pathname, documents]);

  const initializeApp = useCallback(async () => {
    console.log('🚀 AnCore Document Intelligence Engine initialized');
    
    try {
      const healthCheck = await apiService.get('/health');
      console.log('✅ API Health:', healthCheck);
      notificationService.show('API connected successfully', 'success', 3000);
    } catch (error) {
      console.error('❌ API Health Check Failed:', error);
      notificationService.show('API connection failed - some features may be unavailable', 'warning', 8000);
    }

    loadDashboardData();
  }, [loadDashboardData]);

  const setupAutoRefresh = useCallback(() => {
    const interval = setInterval(() => {
      refreshProcessingDocuments();
    }, 5000);

    return () => clearInterval(interval);
  }, [refreshProcessingDocuments]);
 
  const loadProjects = useCallback(async () => {
    try {
      setIsProjectsLoading(true);
      const data = await apiService.get('/projects/');
      setProjects(data.projects);
    } catch (error) {
      console.error('Error loading projects:', error);
      notificationService.show('Failed to load projects', 'error');
    } finally {
      setIsProjectsLoading(false);
    }
  }, [setIsProjectsLoading, setProjects]);

  useEffect(() => {
    initializeApp();
    const cleanupAutoRefresh = setupAutoRefresh();
    loadProjects(); // Call loadProjects here
    return () => {
      cleanupAutoRefresh();
    };
  }, [initializeApp, setupAutoRefresh, loadProjects]);

  const handleOpenQueryTable = (project: Project) => {
    setSelectedProject(project);
    setShowQueryTableFullScreen(true);
  };

  const handleCloseQueryTable = () => {
    setShowQueryTableFullScreen(false);
    setSelectedProject(null);
  };

  // Show full-screen query table if active
  if (showQueryTableFullScreen && selectedProject) {
    return (
      <div style={{
        height: '100vh',
        background: 'var(--surface-color)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <QueryTableFullScreen
          project={selectedProject}
          onBack={handleCloseQueryTable}
        />
        <NotificationContainer />
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      background: 'var(--surface-color)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Sidebar />
      
      <div style={{
        flex: 1,
        overflow: 'auto',
        background: 'var(--surface-color)'
      }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/projects" element={<Projects onOpenQueryTable={handleOpenQueryTable} projects={projects} isLoading={isProjectsLoading} />} />
          <Route path="/upload" element={<Upload onUploadComplete={loadDashboardData} />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/chat" element={<Chat />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="*" element={<Dashboard />} /> {/* Fallback route */}
        </Routes>
      </div>

      <NotificationContainer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthWrapper>
        <MainApp />
      </AuthWrapper>
    </ThemeProvider>
  );
}

export default App;
